import React from "react"
import { Descriptions, Table, Typography, Tag, Button } from "antd"
import moment from "moment"
import { DownloadOutlined } from "@ant-design/icons"

const { Text, Title, Link } = Typography

/**
 * A component to display the key details of an invoice.
 * @param {object} props - The component props.
 * @param {object} props.invoice - The invoice data object.
 */
const InvoiceDetails = ({ invoice }) => {
  // Gracefully handle cases where there is no invoice data
  if (!invoice || !invoice.id) {
    return null
  }

  // --- Data Formatting ---
  // Combine prefix and number, padding the number with leading zeros
  const invoiceNumber = `${invoice.docPrefix || ""}${String(invoice.docNumber || "").padStart(
    4,
    "0"
  )}`

  // Format timestamps using moment.js
  const createdDate = invoice.created
    ? moment.unix(invoice.created.seconds).format("DD.MM.YYYY")
    : "N/A"
  const payUntilDate = invoice.payUntil ? moment.unix(invoice.payUntil.seconds) : null

  // Check if the due date is before today
  const isOverdue = payUntilDate ? payUntilDate.isBefore(moment(), "day") : false

  // --- Table columns for the prices array ---
  const priceColumns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "right",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      align: "right",
      render: (val) => `${(val || 0).toFixed(2)} €`,
    },
    {
      title: "Sum",
      dataIndex: "sum",
      key: "sum",
      align: "right",
      render: (val) => <Text strong>{`${(val || 0).toFixed(2)} €`}</Text>,
    },
  ]

  return (
    <>
      <Title level={5} style={{ marginTop: "20px" }}>
        Invoice Info
      </Title>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Invoice Number">
          <Text strong>{invoiceNumber}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Created Date">{createdDate}</Descriptions.Item>
        <Descriptions.Item label="Payment Due">
          {payUntilDate ? (
            <Tag color={isOverdue ? "red" : "blue"}>{payUntilDate.format("DD.MM.YYYY")}</Tag>
          ) : (
            "N/A"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Language">{invoice.language || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Download">
          <Link href={invoice.downloadUrl} target="_blank" rel="noopener noreferrer">
            <Button icon={<DownloadOutlined />} size="small">
              Download PDF
            </Button>
          </Link>
        </Descriptions.Item>
      </Descriptions>
      <Title level={5} style={{ marginTop: "16px", marginBottom: "8px" }}>
        Products
      </Title>
      <Table
        columns={priceColumns}
        dataSource={invoice.prices}
        rowKey={(record, index) => record.key || `price-${index}`}
        pagination={false}
        size="small"
        bordered
      />
    </>
  )
}

export default InvoiceDetails
